import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { LoginForm } from '@dx-ui/osc-login';
import { JoinModal } from '@dx-ui/osc-join-modal-button';
import { useTranslation } from 'next-i18next';
import { Link } from '@dx-ui/osc-link';
import { DrawerPanel } from '@dx-ui/osc-drawer';
import cx from 'classnames';
import MegaMenu from './mega-menu';
import { DrawerList } from './nav-drawer-list';
import Icon from '@dx-ui/osc-icon';
import {
  dispatchGlobalNavigationClick,
  sendGlobalNavMenuOpenGoal,
  sendInteractionReward,
  SpeechBubbleContext,
} from '../header.utilities';
import { LanguageSelector } from '@dx-ui/osc-language-selector';
import { sendReward } from '@dx-ui/framework-conductrics';
import { HeaderLogoLink } from '../header.logo';
import { HEADER_CLICK_IDS } from '../types';
import type { TBrandHeaderBody } from '../types';
import { useRouter } from 'next/router';
import { getLocaleFormattedNumber } from '@dx-ui/framework-i18n';
import { useActiveUserLinks } from '../hooks/useActiveUserLinks';
import { NavItemWithModal, NavItemWithSpeechBubble } from './nav-items';
import { Surface } from '@dx-ui/global-ui-prism';
import { HEADER_VARIANT_TYPE } from '../header.constants';

export const BrandHeaderBody = ({
  menuItems,
  brand,
  loginOptions,
  user,
  wrapperId,
  onSignInAttempt,
  languageSelectorOptions,
  onSignOut,
  isFluid,
  suppressLogo,
  shouldSuppressFindStay,
  shouldSuppressJoinAndSignIn,
  shouldOpenAccountLinksInNewWindow,
  shouldOpenJoinInNewWindow,
  shouldOpenJoinInPage,
  shouldSuppressHonorsAccountLinks,
  heading,
  mvts,
  surfaceType,
  surfaceClassName,
  headerVariantType,
  globalNavLinks,
  isTranslucent,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
  analytics,
}: TBrandHeaderBody) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const { t } = useTranslation(['osc-header', 'osc-login']);
  const userLoggedInButtonRef = useRef<HTMLButtonElement>(null);

  const activeUserLinks = useActiveUserLinks();

  const closeMenus = useCallback(() => {
    setIsMenuOpen(false);
    setIsAccountOpen(false);
  }, []);

  useEffect(() => {
    if (!isMenuOpen && !isAccountOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen, isAccountOpen, closeMenus]);

  const menuPressed = () => {
    sendInteractionReward();
    sendGlobalNavMenuOpenGoal();
    if (isAccountOpen) setIsAccountOpen(false);
    if (isMenuOpen) {
      setIsMenuOpen(false);
    } else {
      setIsMenuOpen(true);
    }
  };

  const accountPressed = () => {
    sendInteractionReward();
    sendReward('profile-name-click');
    setIsMenuOpen(false);
    if (isAccountOpen) {
      setIsAccountOpen(false);
    } else {
      setIsAccountOpen(true);
    }
  };

  const closeMainMenu = () => {
    setIsMenuOpen(false);
  };

  const closeAccount = () => {
    setIsAccountOpen(false);
  };

  // Uses `resize` + `window.innerWidth` deliberately, not `matchMedia`. When an open drawer
  // sets `overflow: hidden`, Safari can recompute the layout viewport as the scrollbar
  // disappears, making a `min-width: 1024px` media query flip to `matches: true` at a real
  // 1023px window, which would flash the menu open then instantly close it.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) closeMenus();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [closeMenus]);

  const joinButtonClasses = cx(
    'text--xs m-auto inline-block border-e border-surface pe-2 font-semibold brand-ey:font-normal brand-wa:font-normal'
  );

  const { isShowSpeechBubble, isBookSpeechBubble, onDialogOpen } = useContext(SpeechBubbleContext);

  const isHamburgerNavVariant = headerVariantType === HEADER_VARIANT_TYPE.hamburgerNav;
  const isGlobalNavLinksAvailable = globalNavLinks && globalNavLinks.length > 0;
  const isMenuItemsAvailable = menuItems && menuItems.length > 0;

  return (
    <Surface
      type={surfaceType || 'base'}
      className={cx('osc-header-wrapper', surfaceClassName, {
        '!bg-translucent': isTranslucent,
      })}
    >
      {/* Gate `lg:hidden` on menu state: if applied while a menu is open, crossing 1024px would
          hide the drawer, hamburger, and click-away overlay via CSS while overflow:hidden is
          still applied, trapping the user with no way to close it (NHCGUEST-10538). */}
      <nav
        id="drawer-nav"
        data-testid="drawer-nav"
        className={cx('z-50', { 'lg:hidden': !isMenuOpen && !isAccountOpen })}
      >
        <div
          className={cx(
            'flex items-center',
            isHamburgerNavVariant ? 'justify-between' : 'justify-end'
          )}
        >
          {(isMenuItemsAvailable || isGlobalNavLinksAvailable) && isHamburgerNavVariant ? (
            <button
              className={cx('me-2 w-12 p-2 md:px-8 lg:px-10', {
                'self-end sm:self-center': !!languageSelectorOptions,
              })}
              onClick={menuPressed}
              type="button"
              id="menu-open-button"
              data-testid="hamburger-menu"
              aria-haspopup
            >
              <Icon name="menu" size="md" className="m-auto block" />
              <span className="text--xs font-semibold brand-ey:font-normal brand-wa:font-normal">
                <span className="sr-only">{t('openMenu')}</span>
              </span>
            </button>
          ) : null}
          {languageSelectorOptions ? (
            <div className="pe-1 pt-2 md:pe-7">
              <LanguageSelector {...languageSelectorOptions} />
            </div>
          ) : null}
        </div>
        <div
          className={cx('flex content-center items-center p-2 md:px-8 lg:px-10', {
            'sm:h-16': !!languageSelectorOptions,
            'h-min px-2 py-1': !languageSelectorOptions,
          })}
        >
          {isMenuItemsAvailable && !isHamburgerNavVariant ? (
            <button
              className={cx('me-2 w-12', {
                'self-center lg:self-end': !!languageSelectorOptions,
              })}
              onClick={menuPressed}
              type="button"
              aria-haspopup
              id="menu-open-button"
              data-testid="hamburger-menu"
            >
              <Icon name="menu" size="md" className="m-auto block" />
              <span className="text--xs font-semibold brand-ey:font-normal brand-wa:font-normal">
                <span className="sr-only">{t('openMenu')}</span>
              </span>
            </button>
          ) : null}
          {!suppressLogo ? (
            <HeaderLogoLink
              surfaceType={surfaceType}
              brand={brand}
              className={cx({
                'self-end sm:self-center': !!languageSelectorOptions,
              })}
              heading={heading}
            />
          ) : null}
          <div className="ms-auto flex flex-col items-center gap-x-2 sm:flex-row">
            <div className="ms-auto flex items-center gap-x-2">
              {activeUserLinks?.findStayLink && !shouldSuppressFindStay ? (
                <Link
                  className="mb-1"
                  anchorClassName="text-center min-w-16 w-min align-middle"
                  underline={false}
                  url={activeUserLinks?.findStayLink.url}
                  isNewWindow={shouldOpenAccountLinksInNewWindow}
                  showNewWindowIcon={false}
                  onClick={() => {
                    sendInteractionReward();
                    sendReward('find-stay-click');
                    dispatchGlobalNavigationClick(
                      HEADER_CLICK_IDS.FindStay,
                      onGlobalNavigationClick,
                      isLegacyGlobalNavigationClickAnalyticsEnabled
                    );
                  }}
                >
                  <Icon name="calendar-search" size="md" className="m-auto block" />
                  <span className="text--xs inline-block font-semibold brand-ey:font-normal brand-wa:font-normal">
                    {t('yourStays')}
                  </span>
                </Link>
              ) : null}

              {user ? (
                <button
                  className="items-center"
                  onClick={() => {
                    dispatchGlobalNavigationClick(
                      HEADER_CLICK_IDS.MemberName,
                      onGlobalNavigationClick,
                      isLegacyGlobalNavigationClickAnalyticsEnabled
                    );
                    accountPressed();
                  }}
                  ref={userLoggedInButtonRef}
                  aria-expanded={isAccountOpen}
                  type="button"
                >
                  <Icon name="user-circle" size="md" className="m-auto block" />
                  <span className="text--xs break-all font-semibold brand-ey:font-normal brand-wa:font-normal">
                    {t('greetings', { username: user.name })}
                  </span>
                </button>
              ) : (activeUserLinks?.signInLink || activeUserLinks?.signUpLink) &&
                !shouldSuppressJoinAndSignIn ? (
                <div className="flex flex-col pt-7">
                  <div className="flex items-center">
                    {activeUserLinks?.signUpLink ? (
                      shouldOpenJoinInPage ? (
                        <div className="h-fit">
                          <Link
                            {...activeUserLinks.signUpLink}
                            showNewWindowIcon={false}
                            isNewWindow={shouldOpenJoinInNewWindow}
                            underline={false}
                            className={joinButtonClasses}
                            onClick={() => {
                              sendInteractionReward();
                              sendReward('nav-join-click');
                              dispatchGlobalNavigationClick(
                                HEADER_CLICK_IDS.Join,
                                onGlobalNavigationClick,
                                isLegacyGlobalNavigationClickAnalyticsEnabled
                              );
                            }}
                          >
                            {t('signUp')}
                          </Link>
                        </div>
                      ) : (
                        <div className="h-fit">
                          <JoinModal
                            analytics={analytics}
                            language={languageSelectorOptions?.locale || 'en'}
                            onLoginAttempt={onSignInAttempt}
                            dialogTrigger={
                              <button
                                type="button"
                                className={joinButtonClasses}
                                onClick={() => {
                                  sendInteractionReward();
                                  sendReward('nav-join-click');
                                  dispatchGlobalNavigationClick(
                                    HEADER_CLICK_IDS.Join,
                                    onGlobalNavigationClick,
                                    isLegacyGlobalNavigationClickAnalyticsEnabled
                                  );
                                }}
                              >
                                {t('signUp')}
                              </button>
                            }
                            onSuccessfulLogin={() => {
                              setTimeout(() => userLoggedInButtonRef.current?.focus(), 1000);
                            }}
                          />
                        </div>
                      )
                    ) : null}
                    {activeUserLinks.signInLink && isShowSpeechBubble ? (
                      <div
                        className="ps-2"
                        onClick={() => {
                          sendInteractionReward();
                          dispatchGlobalNavigationClick(
                            HEADER_CLICK_IDS.SignIn,
                            onGlobalNavigationClick,
                            isLegacyGlobalNavigationClickAnalyticsEnabled
                          );
                        }}
                      >
                        <NavItemWithSpeechBubble
                          onLoginAttempt={onSignInAttempt}
                          analytics={analytics}
                          buttonClassName="text--xs font-semibold brand-ey:font-normal brand-wa:!font-normal"
                          onDialogOpen={() => {
                            setIsMenuOpen(false);
                            setIsAccountOpen(false);
                            onDialogOpen?.();
                          }}
                          shouldOpenJoinInPage={shouldOpenJoinInPage}
                          locale={languageSelectorOptions?.locale || 'en'}
                          loggedInButtonRef={userLoggedInButtonRef}
                          {...loginOptions}
                        >
                          <Icon
                            name="user-circle"
                            size="md"
                            className={cx('-ms-[22px] -mt-7 block', {
                              'mb-1': isBookSpeechBubble,
                            })}
                          />
                          <span>{t('osc-login:signIn')}</span>
                        </NavItemWithSpeechBubble>
                      </div>
                    ) : null}
                    {activeUserLinks.signInLink && !isShowSpeechBubble ? (
                      <div
                        className="ps-2"
                        onClick={() => {
                          sendInteractionReward();
                          dispatchGlobalNavigationClick(
                            HEADER_CLICK_IDS.SignIn,
                            onGlobalNavigationClick,
                            isLegacyGlobalNavigationClickAnalyticsEnabled
                          );
                        }}
                      >
                        <div className="-mt-7">
                          <Icon name="user-circle" size="md" className="-ms-[22px] block" />
                        </div>

                        <NavItemWithModal
                          loggedInButtonRef={userLoggedInButtonRef}
                          onLoginAttempt={onSignInAttempt}
                          analytics={analytics}
                          section="user"
                          shouldOpenJoinInPage={shouldOpenJoinInPage}
                          locale={languageSelectorOptions?.locale || 'en'}
                          dialogTrigger={
                            <button
                              type="button"
                              className="text--xs font-semibold brand-ey:font-normal brand-wa:!font-normal"
                            >
                              {t('osc-login:signIn')}
                            </button>
                          }
                        >
                          <LoginForm
                            onLoginAttempt={onSignInAttempt}
                            {...loginOptions}
                            sourceId="OHW"
                            isInModal={true}
                          />
                        </NavItemWithModal>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {(isMenuOpen || isAccountOpen) && (
          <div
            className="absolute top-0 z-50 h-screen w-full animate-fadein overflow-hidden bg-overlay backdrop-blur-[2px]"
            data-testid="backgroundOverlay"
            onClick={closeMenus}
          />
        )}
        {isMenuItemsAvailable || isGlobalNavLinksAvailable ? (
          <div
            data-testid="menuWindow"
            className={cx({ visible: isMenuOpen, invisible: !isMenuOpen })}
          >
            <Surface type={surfaceType || 'base'}>
              <DrawerPanel
                isOpen={isMenuOpen}
                alignment="left"
                appWrapperId={wrapperId}
                interactiveElementWrapperId="drawer-nav"
                returnFocus={() => {
                  document.getElementById('menu-open-button')?.focus();
                  return false;
                }}
                className={cx('osc-nav-drawer', {
                  '!bg-translucent bg-blur': isTranslucent,
                })}
              >
                <DrawerList
                  isOpen={isMenuOpen}
                  menuItems={menuItems}
                  onClose={closeMainMenu}
                  brand={brand}
                  surfaceType={surfaceType}
                  heading={heading}
                  languageSelectorOptions={languageSelectorOptions}
                  headerVariantType={headerVariantType}
                  onGlobalNavigationClick={onGlobalNavigationClick}
                  isLegacyGlobalNavigationClickAnalyticsEnabled={
                    isLegacyGlobalNavigationClickAnalyticsEnabled
                  }
                  {...(isHamburgerNavVariant && isGlobalNavLinksAvailable && { globalNavLinks })}
                  suppressLogo={suppressLogo}
                />
              </DrawerPanel>
            </Surface>
          </div>
        ) : null}
        <DrawerPanel
          isOpen={isAccountOpen}
          alignment="right"
          appWrapperId={wrapperId}
          interactiveElementWrapperId="drawer-nav"
          className={cx('osc-nav-drawer', {
            '!bg-translucent bg-blur': isTranslucent,
          })}
        >
          <Surface
            type={surfaceType || 'base'}
            className={cx('osc-nav-drawer h-full', {
              '!bg-transparent': isTranslucent,
            })}
          >
            {user && isAccountOpen ? (
              <AccountSection
                user={user}
                userLinks={activeUserLinks}
                onClose={closeAccount}
                onSignOut={onSignOut}
                shouldSuppressHonorsAccountLinks={shouldSuppressHonorsAccountLinks}
                shouldOpenAccountLinksInNewWindow={shouldOpenAccountLinksInNewWindow}
                onGlobalNavigationClick={onGlobalNavigationClick}
                isLegacyGlobalNavigationClickAnalyticsEnabled={
                  isLegacyGlobalNavigationClickAnalyticsEnabled
                }
              />
            ) : null}
          </Surface>
        </DrawerPanel>
      </nav>

      <nav className="hidden lg:block">
        <MegaMenu
          isFluid={isFluid}
          suppressLogo={suppressLogo}
          shouldSuppressHonorsAccountLinks={shouldSuppressHonorsAccountLinks}
          shouldSuppressFindStay={shouldSuppressFindStay}
          shouldSuppressJoinAndSignIn={shouldSuppressJoinAndSignIn}
          shouldOpenAccountLinksInNewWindow={shouldOpenAccountLinksInNewWindow}
          shouldOpenJoinInNewWindow={shouldOpenJoinInNewWindow}
          shouldOpenJoinInPage={shouldOpenJoinInPage}
          brand={brand}
          onSignInAttempt={onSignInAttempt}
          onSignOut={onSignOut}
          user={user}
          userLinks={activeUserLinks}
          mainNavLinks={menuItems}
          languageSelectorOptions={languageSelectorOptions}
          loginOptions={loginOptions}
          heading={heading}
          mvts={mvts}
          surfaceType={surfaceType}
          headerVariantType={headerVariantType}
          globalNavLinks={globalNavLinks}
          isTranslucent={isTranslucent}
          isHamburgerNavVariant={isHamburgerNavVariant}
          onGlobalNavigationClick={onGlobalNavigationClick}
          isLegacyGlobalNavigationClickAnalyticsEnabled={
            isLegacyGlobalNavigationClickAnalyticsEnabled
          }
          analytics={analytics}
        />
      </nav>
    </Surface>
  );
};

export type TAccountSection = Pick<
  TBrandHeaderBody,
  | 'userLinks'
  | 'onSignOut'
  | 'shouldSuppressHonorsAccountLinks'
  | 'shouldOpenAccountLinksInNewWindow'
  | 'onGlobalNavigationClick'
  | 'isLegacyGlobalNavigationClickAnalyticsEnabled'
> &
  Required<Pick<TBrandHeaderBody, 'user'>> & {
    onClose: () => void;
  };

const AccountUserLink = ({
  url,
  onClick,
  children,
  isNewWindow,
}: {
  url: string;
  onClick: () => void;
  children: React.ReactNode;
  isNewWindow?: boolean;
}) => {
  return (
    <Link
      anchorClassName="block border-surface px-6 py-2 focus:osc-nav-drawer-link-active hover:osc-nav-drawer-link-active"
      className="font-bold text-surface brand-ey:font-normal brand-gu:font-mono brand-gu:font-normal brand-hi:font-normal brand-nd:font-mono brand-nd:font-medium"
      underline={false}
      url={url}
      onClick={onClick}
      isNewWindow={isNewWindow}
    >
      {children}
    </Link>
  );
};

const AccountSection = ({
  user,
  userLinks,
  onClose,
  onSignOut,
  shouldSuppressHonorsAccountLinks,
  shouldOpenAccountLinksInNewWindow,
  onGlobalNavigationClick,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true,
}: TAccountSection) => {
  const { locale = 'en' } = useRouter();

  const { t } = useTranslation('osc-header');
  const formattedHonorsPointsTotal = getLocaleFormattedNumber(user.honorsPoints, { locale });

  return (
    <FocusLock returnFocus>
      <div className="relative mb-2 border-b border-surface px-4 py-3 ">
        <button onClick={onClose} type="button" className="px-2">
          <Icon name="close" size="md" className="m-auto" />
          <span className="text--xs font-semibold brand-ey:font-normal brand-wa:font-normal">
            {t('close')}
            <span className="sr-only">{t('closeAccountDetails')}</span>
          </span>
        </button>
      </div>
      <div className="flex">
        <div className="px-4">
          <div className="flex size-12 items-center justify-center rounded-full bg-hilton-alt p-2">
            <Icon name="user" size="xl" />
          </div>
        </div>
        <div className="font-bold brand-ey:font-normal brand-gu:font-mono brand-gu:font-normal brand-hi:font-normal brand-nd:font-mono brand-nd:font-medium">
          <span>{t('greetings', { username: user.name })}</span>
          <div className="capitalize">
            <span className="underline">{user.honorsTier}</span> {t('status')}
          </div>
          <div>{t('honorsPointsTotal', { honorsPointsTotal: formattedHonorsPointsTotal })}</div>
          {user.hhonorsNumber ? (
            <div>{t('hiltonHonorsNumber', { hhonorsNumber: user.hhonorsNumber })}</div>
          ) : null}
        </div>
      </div>
      <div className="mt-4 divide-y divide-solid border-y border-surface">
        {userLinks?.accountLink?.url ? (
          <AccountUserLink
            url={userLinks.accountLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameHiltonHonorsHome,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('hiltonHonorsHome')}
          </AccountUserLink>
        ) : null}
        {userLinks?.honorsProfileLink?.url ? (
          <AccountUserLink
            url={userLinks.honorsProfileLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                isLegacyGlobalNavigationClickAnalyticsEnabled
                  ? HEADER_CLICK_IDS.MemberNameHiltonHonorsProfile
                  : HEADER_CLICK_IDS.MemberNameProfile,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('profile')}
          </AccountUserLink>
        ) : null}

        {userLinks?.honorsActivityLink?.url && !shouldSuppressHonorsAccountLinks ? (
          <AccountUserLink
            url={userLinks.honorsActivityLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                isLegacyGlobalNavigationClickAnalyticsEnabled
                  ? HEADER_CLICK_IDS.MemberNameHiltonHonorsActivity
                  : HEADER_CLICK_IDS.MemberNameActivity,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('activity')}
          </AccountUserLink>
        ) : null}

        {userLinks?.yourOffersLink?.url && !shouldSuppressHonorsAccountLinks ? (
          <AccountUserLink
            url={userLinks.yourOffersLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameHiltonHonorsYourOffers,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('yourOffers')}
          </AccountUserLink>
        ) : null}

        {userLinks?.honorsPointsLink?.url && !shouldSuppressHonorsAccountLinks ? (
          <AccountUserLink
            url={userLinks.honorsPointsLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                isLegacyGlobalNavigationClickAnalyticsEnabled
                  ? HEADER_CLICK_IDS.MemberNameHiltonHonorsPoints
                  : HEADER_CLICK_IDS.MemberNamePoints,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('pointsLabel')}
          </AccountUserLink>
        ) : null}

        {userLinks?.goHiltonLink?.url ? (
          <AccountUserLink
            url={userLinks.goHiltonLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameGoHiltonProgramDetails,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('goHiltonProgramDetails')}
          </AccountUserLink>
        ) : null}

        {userLinks?.friendsAndFamilyLink?.url ? (
          <AccountUserLink
            url={userLinks.friendsAndFamilyLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameManageFriendsAndFamily,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('manageFriendsAndFamily')}
          </AccountUserLink>
        ) : null}

        {userLinks?.hgvMaxLink?.url ? (
          <AccountUserLink
            url={userLinks.hgvMaxLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameHgvMaxRateDetails,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {t('hgvMaxRateDetails')}
          </AccountUserLink>
        ) : null}

        {userLinks?.b2bMemberLink?.url ? (
          <AccountUserLink
            url={userLinks.b2bMemberLink.url}
            onClick={() => {
              dispatchGlobalNavigationClick(
                HEADER_CLICK_IDS.MemberNameManageHiltonForBusiness,
                onGlobalNavigationClick,
                isLegacyGlobalNavigationClickAnalyticsEnabled
              );
            }}
            isNewWindow={shouldOpenAccountLinksInNewWindow}
          >
            {userLinks?.b2bMemberLink?.label}
          </AccountUserLink>
        ) : null}

        <button
          className="focus:osc-nav-drawer-link-active hover:osc-nav-drawer-link-active block w-full border-surface px-6 py-2 text-left font-bold text-surface brand-ey:font-normal brand-gu:font-mono brand-gu:font-normal brand-hi:font-normal brand-nd:font-mono brand-nd:font-medium rtl:text-start"
          onClick={async () => {
            dispatchGlobalNavigationClick(
              HEADER_CLICK_IDS.MemberNameSignOut,
              onGlobalNavigationClick,
              isLegacyGlobalNavigationClickAnalyticsEnabled
            );
            onClose();
            await onSignOut();
          }}
          type="button"
        >
          {t('signOut')}
        </button>
      </div>
    </FocusLock>
  );
};
